import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import useAppStoreLinks from '../../hooks/useAppStoreLinks';
import { APP_AUDIENCES, findAvailableStoreLink } from '../../utils/appStoreLinks';
import parentsArtwork from '../../assets/icons/portal/parent-login.png';
import kidsArtwork from '../../assets/icons/portal/kid-login.png';
import './AppDownloadCard.css';

const STORES = [
  { platform: 'ios', name: 'App Store', Icon: FaApple },
  { platform: 'android', name: 'Google Play', Icon: FaGooglePlay },
];

export default function AppDownloadCard({ audience }) {
  const { links, loading, error, retry } = useAppStoreLinks();
  const [openError, setOpenError] = useState('');
  const isNative = Capacitor.isNativePlatform();
  if (!APP_AUDIENCES.includes(audience)) return null;
  const parents = audience === 'parents';
  const title = `MyPiggy ${parents ? 'Parents' : 'Kids'}`;

  return (
    <article aria-label={`${title} downloads`} className={`app-download-card rounded-3xl border bg-white/95 text-center shadow-sm dark:bg-slate-900/90 ${parents ? 'border-teal-200 dark:border-teal-800' : 'border-pink-200 dark:border-pink-900'}`}>
      <div className="app-download-card__header">
        <img src={parents ? parentsArtwork : kidsArtwork} alt="" className="app-download-card__artwork" />
        <div className="min-w-0 text-left">
          <h2 className={`text-xl font-extrabold ${parents ? 'text-teal-900 dark:text-teal-200' : 'text-pink-700 dark:text-pink-300'}`}>{title}</h2>
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Get the app on your phone</p>
        </div>
      </div>
      <div className="app-download-card__stores">
        {STORES.map(({ platform, name, Icon }) => {
          const storeLink = !loading && !error ? findAvailableStoreLink(links, audience, platform) : null;
          const available = Boolean(storeLink);
          const status = loading ? 'Checking…' : error ? 'Unavailable' : available ? 'Download' : 'Coming soon';
          const className = 'app-download-card__store rounded-2xl border border-slate-700 bg-slate-950 text-white';
          const content = <><Icon aria-hidden="true" className="app-download-card__store-icon" /><span className="app-download-card__store-copy"><span className="text-sm font-bold">{name}</span><span className="text-xs text-slate-300">{status}</span></span></>;
          return available ? (
            <a key={platform} href={isNative ? storeLink : `/download/${audience}/${platform}`} onClick={async (event) => {
              if (!isNative) return;
              event.preventDefault();
              setOpenError('');
              try { await Browser.open({ url: storeLink }); }
              catch { setOpenError('Unable to open the store. Please try the download button again.'); }
            }} target="_blank" rel="noopener noreferrer" aria-label={`Download ${title} on ${name} ${isNative ? '(opens store link)' : '(opens in a new tab)'}`} className={`${className} transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-500 focus-visible:ring-offset-2`}>
              {content}
            </a>
          ) : <div key={platform} aria-disabled="true" aria-label={`${title}, ${name}: ${status}`} className={`${className} opacity-70`}>{content}</div>;
        })}
      </div>
      {openError && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{openError}</p>}
      {error && <p role="status" className="mt-3 text-sm text-slate-600 dark:text-slate-300">Downloads are temporarily unavailable. <button type="button" onClick={retry} className="font-bold underline underline-offset-2">Try again</button></p>}
    </article>
  );
}
